import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, Copy, Send, AlertTriangle } from "lucide-react";
import { Button, Field, Input, Textarea, Select } from "@/design/components";
import { useCreateInvite } from "@/domain/useOnboarding";
import { useInvitableProjects, useActiveProfile } from "@/domain/hooks";
import type { InvitableProject, InvitableSubProject } from "@/domain/api";
import { roleLabelFor, CONTRACTOR_WHOLE_PROJECT_ROLES, CONTRACTOR_SUBPROJECT_ROLES } from "@/domain/cascadeRoles";

// Spec §9.5 — reusable "invite someone" modal. Surfaces the project / sub-project
// scope (the caller's projects, and for the multi-project case any of them), the
// role choice (scoped to what the current user can appoint on the chosen scope),
// and an optional message; on confirm a secure invite link is generated and an
// email is sent automatically to the invitee. The link is also shown as a
// backup for manual sharing — and as the primary path if the email fails
// (the backend reports delivery via `emailSent`).

// The "whole project" sentinel for the sub-project selector.
const WHOLE_PROJECT = "";

export function InviteModal({
  open,
  onOpenChange,
  projectId,
  projectName,
  defaultProjectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number | string;
  projectName?: string;
  /** Project to preselect when launched from a project view. Falls back to
   * `projectId`. The user can still switch to any of their other projects. */
  defaultProjectId?: number | string;
}) {
  const invitable = useInvitableProjects();
  const profile = useActiveProfile();
  const { createInvite, isPending, error } = useCreateInvite();

  const preferredProjectId = String(defaultProjectId ?? projectId ?? "");

  // Selected top-level project + sub-project ("" = whole project).
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedSubId, setSelectedSubId] = useState<string>(WHOLE_PROJECT);
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const [returningUser, setReturningUser] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [queued, setQueued] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  // True when the project this modal was launched from isn't in the user's
  // invitable list, so the default selection below had to fall back to some
  // other project. Surfaced in the UI — silently substituting a different
  // project here previously sent invites to the wrong project with no
  // indication anything had changed.
  const [projectMismatch, setProjectMismatch] = useState(false);

  // Default the project selection to the launching project once data arrives.
  useEffect(() => {
    if (selectedProjectId || invitable.length === 0) return;
    const match = invitable.find(
      (p) =>
        String(p.projectId) === preferredProjectId ||
        p.subProjects.some((sp) => String(sp.projectId) === preferredProjectId),
    );
    setProjectMismatch(!match);
    setSelectedProjectId(String((match ?? invitable[0]).projectId));
  }, [invitable, preferredProjectId, selectedProjectId]);

  const selectedProject: InvitableProject | undefined = useMemo(
    () => invitable.find((p) => String(p.projectId) === selectedProjectId),
    [invitable, selectedProjectId],
  );

  const selectedSub: InvitableSubProject | undefined = useMemo(
    () => selectedProject?.subProjects.find((s) => String(s.projectId) === selectedSubId),
    [selectedProject, selectedSubId],
  );

  // Roles come from the chosen scope: a picked sub-project, else the project.
  const scopeNode: InvitableSubProject | undefined = selectedSub ?? selectedProject;
  const isSubScope = !!selectedSub;
  const roleOptions = useMemo(() => {
    // Contractors follow the fixed cascade rule, narrowed by whole-project vs
    // sub-project scope. Everyone else uses the roles the data source allows.
    const keys =
      profile.group === "contractor"
        ? isSubScope
          ? CONTRACTOR_SUBPROJECT_ROLES
          : CONTRACTOR_WHOLE_PROJECT_ROLES
        : scopeNode?.appointableRoles ?? [];
    return keys.map((key) => ({ key, label: roleLabelFor(key) }));
  }, [profile.group, isSubScope, scopeNode]);

  // Reset the sub-project scope when the project changes; if the whole project
  // isn't invitable (the user only leads sub-projects of it), default to its
  // first invitable sub-project.
  useEffect(() => {
    if (!selectedProject) return;
    if (selectedProject.appointableRoles.length === 0 && selectedProject.subProjects.length > 0) {
      setSelectedSubId(String(selectedProject.subProjects[0].projectId));
    } else {
      setSelectedSubId(WHOLE_PROJECT);
    }
    // Only re-run when the selected project changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // Keep the selected role valid as the scope changes.
  useEffect(() => {
    if (roleOptions.length === 0) {
      if (role) setRole("");
      return;
    }
    if (!roleOptions.some((o) => o.key === role)) setRole(roleOptions[0].key);
  }, [roleOptions, role]);

  // The project id actually sent: the chosen scope's invite target (a
  // sub-project is itself a project the inviter has authority on; in demo mode
  // it falls back to the parent project's slug via inviteTargetId), else the
  // top-level project.
  const targetProjectId =
    scopeNode?.inviteTargetId ?? scopeNode?.projectId ?? projectId;

  const canSend =
    /.+@.+\..+/.test(email.trim()) &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    !!role &&
    targetProjectId !== undefined;

  function reset() {
    setSelectedProjectId("");
    setSelectedSubId(WHOLE_PROJECT);
    setRole("");
    setEmail("");
    setFirstName("");
    setLastName("");
    setMessage("");
    setLink(null);
    setReturning(false);
    setReturningUser(false);
    setEmailSent(false);
    setQueued(false);
    setSubmitted(false);
    setCopied(false);
    setProjectMismatch(false);
  }

  async function onSend() {
    if (!role) return;
    const result = await createInvite({
      role,
      inviteeEmail: email.trim(),
      inviteeName: `${firstName.trim()} ${lastName.trim()}`.trim(),
      projectId: targetProjectId,
      message: message.trim() || undefined,
    });
    if (result) {
      setReturning(result.returningOrg);
      setReturningUser(result.returningUser);
      setQueued(result.queued ?? false);
      // A token means a real onboarding link to show/copy. No token: either the
      // invite was queued (inviter's org not approved yet — emailed on approval,
      // spec §9) or an existing active org matched (nothing to onboard).
      if (result.token) {
        setLink(`${window.location.origin}/onboard/${result.token}`);
        setEmailSent(result.emailSent ?? false);
      }
      setSubmitted(true);
    }
  }

  async function copyLink() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the link is still selectable */
    }
  }

  const roleLabel = roleOptions.find((r) => r.key === role)?.label ?? role;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted
                ? queued
                  ? "Invite queued"
                  : link
                    ? emailSent
                      ? "Invite sent"
                      : "Invite created"
                    : "Already on Impact Ledger"
                : "Invite someone"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted && queued ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-line bg-subtle px-3 py-3 text-sm text-mute">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span>
                  Invite queued for <strong>{email}</strong> as {roleLabel}. It'll be
                  emailed automatically once your organisation is approved — nothing is
                  sent to them before then.
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Invite another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : submitted && !link ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {returningUser ? (
                    <><strong>{email}</strong> already has an Impact Ledger account, so
                    they've been added straight to this project as {roleLabel} — no
                    onboarding link needed.</>
                  ) : (
                    <><strong>{email}</strong>'s organisation is already active on Impact
                    Ledger, so it's been linked to this project — no onboarding needed.</>
                  )}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Invite another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : link ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              {emailSent ? (
                <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Invite sent to <strong>{email}</strong> as {roleLabel}
                    {returning ? " (existing organisation matched)" : ""}.
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-md border border-warn bg-warnSoft px-3 py-3 text-sm text-warnStrong">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Invite created for <strong>{email}</strong> as {roleLabel}
                    {returning ? " (existing organisation matched)" : ""}, but the
                    email could not be sent. Share the link below with them directly.
                  </span>
                </div>
              )}
              <Field label={emailSent ? "Backup: invite link" : "Invite link"}>
                <div className="flex items-center gap-2">
                  <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
                  <Button variant="secondary" onClick={copyLink} aria-label="Copy link">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </Field>
              <p className="text-xs text-faint">
                {emailSent
                  ? "A secure onboarding link has been emailed to the recipient — the link above is the same one, in case you also want to share it another way. It expires in 30 days."
                  : "This single-use link is the only copy — send it to the recipient yourself (email or WhatsApp). It expires in 30 days."}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Invite another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Email a secure invite scoped to a project you lead
                {invitable.length === 0 && projectName ? <> ({projectName})</> : null}. They complete their own onboarding.
              </Dialog.Description>

              {invitable.length === 0 ? (
                <p className="rounded-md border border-line bg-subtle px-3 py-3 text-sm text-mute">
                  You don't have any projects you can invite people to yet.
                </p>
              ) : (
                <>
                  {projectMismatch && (
                    <div className="flex items-start gap-3 rounded-md border border-warn bg-warnSoft px-3 py-3 text-sm text-warnStrong">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        You don't have invite permissions on
                        {projectName ? <> <strong>{projectName}</strong></> : " this project"}
                        , so a different project is selected below — double-check it
                        before sending.
                      </span>
                    </div>
                  )}
                  <Field label="Project">
                    <Select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)}>
                      {invitable.map((p) => (
                        <option key={String(p.projectId)} value={String(p.projectId)}>{p.name}</option>
                      ))}
                    </Select>
                  </Field>

                  {selectedProject && selectedProject.subProjects.length > 0 && (
                    <Field label="Sub-project scope">
                      <Select value={selectedSubId} onChange={(e) => setSelectedSubId(e.target.value)}>
                        <option value={WHOLE_PROJECT} disabled={selectedProject.appointableRoles.length === 0}>
                          Whole project
                        </option>
                        {selectedProject.subProjects.map((s) => (
                          <option key={String(s.projectId)} value={String(s.projectId)}>{s.name}</option>
                        ))}
                      </Select>
                    </Field>
                  )}

                  <Field label="Role">
                    <Select value={role} onChange={(e) => setRole(e.target.value)} disabled={roleOptions.length === 0}>
                      {roleOptions.length === 0 ? (
                        <option value="">No roles you can appoint here</option>
                      ) : (
                        roleOptions.map((r) => (
                          <option key={r.key} value={r.key}>{r.label}</option>
                        ))
                      )}
                    </Select>
                  </Field>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name">
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="e.g. Grace" />
                </Field>
                <Field label="Last name">
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="e.g. Nansubuga" />
                </Field>
              </div>
              <Field label="Their email">
                <Input value={email} type="email" onChange={(e) => setEmail(e.target.value)} placeholder="director@contractor.co.ug" />
              </Field>
              <Field label="Personal message (optional)">
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="A short note to include with the invite." />
              </Field>
              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && !link && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSend || isPending} onClick={onSend}>
                <Send className="h-4 w-4" /> {isPending ? "Sending…" : "Send invite"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
